<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'

// Thin top-of-viewport progress bar for route navigation. Self-contained:
// registers its own router hooks and cleans them up on unmount. Only appears
// when a navigation takes longer than SHOW_DELAY_MS (lazy chunks, slow guards)
// so instant navigations never flicker. Trickles toward 80% and completes on
// afterEach/onError.
const SHOW_DELAY_MS = 150
const TRICKLE_INTERVAL_MS = 200
const DONE_HIDE_DELAY_MS = 250

const router = useRouter()
const active = ref(false)
const progress = ref(0)

let delayTimer: ReturnType<typeof setTimeout> | null = null
let trickleTimer: ReturnType<typeof setInterval> | null = null
let doneTimer: ReturnType<typeof setTimeout> | null = null

function clearTimers() {
	if (delayTimer) clearTimeout(delayTimer)
	if (trickleTimer) clearInterval(trickleTimer)
	if (doneTimer) clearTimeout(doneTimer)
	delayTimer = trickleTimer = doneTimer = null
}

function start() {
	clearTimers()
	delayTimer = setTimeout(() => {
		active.value = true
		progress.value = 12
		trickleTimer = setInterval(() => {
			progress.value = Math.min(80, progress.value + (80 - progress.value) * 0.12 + 0.5)
		}, TRICKLE_INTERVAL_MS)
	}, SHOW_DELAY_MS)
}

function finish() {
	clearTimers()
	if (!active.value) {
		progress.value = 0
		return
	}
	progress.value = 100
	doneTimer = setTimeout(() => {
		active.value = false
		progress.value = 0
	}, DONE_HIDE_DELAY_MS)
}

const removeBefore = router.beforeEach(() => {
	start()
})
const removeAfter = router.afterEach(() => {
	finish()
})
const removeError = router.onError(() => {
	finish()
})

onUnmounted(() => {
	removeBefore()
	removeAfter()
	removeError()
	clearTimers()
})

const barVars = computed(() => ({ '--route-progress': String(progress.value / 100) }))
</script>

<template>
	<div
		v-show="active"
		class="route-progress"
		:style="barVars"
		aria-hidden="true"
	>
		<div class="route-progress__bar" />
	</div>
</template>

<style scoped lang="scss">
// z-index 3000: above the GlobalLoader overlay (2900)
.route-progress {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	height: 3px;
	z-index: 3000;
	pointer-events: none;

	&__bar {
		height: 100%;
		background: rgb(var(--v-theme-primary));
		transform-origin: left;
		transform: scaleX(var(--route-progress, 0));
		transition: transform 200ms ease;
	}
}
</style>
